import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  ScrollView as RNScrollView,
  Text,
  TextInput as RNTextInput,
  View,
} from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { LiquidCanvas } from '@/components/ui/LiquidBackdrop'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle, MapPin, Search, X } from '@/components/ui/icons'

import { LocationMapPicker } from '@/components/hosting/LocationMapPicker'
import { Button } from '@/components/ui/Button'
import { TrackedNativePressable as RNPressable } from '@/components/ui/TrackedNativePressable'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { geocodingService } from '@/lib/services/geocoding.service'
import { useSemanticColors } from '@/lib/theme/tokens'
import {
  haversineKm,
  type BookingLocationValue,
  isWithinDeliveryRadius,
} from '@/lib/utils/booking-location'
import { captureError } from '@/lib/utils/observability'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'

export type BookingLocationPickerMode = 'pickup' | 'dropoff'
export type { BookingLocationValue }

interface GeocodingResult extends BookingLocationValue {
  city: string | null
  country: string | null
}

interface BookingLocationPickerProps {
  visible: boolean
  mode: BookingLocationPickerMode
  initialLocation: BookingLocationValue
  hostLocation: { lat: number; lng: number }
  deliveryRadiusKm: number
  onClose: () => void
  onSave: (location: BookingLocationValue) => void
}

export function BookingLocationPicker({
  visible,
  mode,
  initialLocation,
  hostLocation,
  deliveryRadiusKm,
  onClose,
  onSave,
}: BookingLocationPickerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const [selected, setSelected] = useState<BookingLocationValue>(initialLocation)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeocodingResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isResolvingPin, setIsResolvingPin] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [mapRevision, setMapRevision] = useState(0)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reverseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<RNTextInput>(null)

  useEffect(() => {
    if (!visible) return
    setSelected(initialLocation)
    setMapRevision((revision) => revision + 1)
    setQuery('')
    setResults([])
    setHasSearched(false)
    setIsSearching(false)
    setIsResolvingPin(false)
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 260)
    return () => clearTimeout(focusTimer)
  }, [initialLocation, visible])

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (reverseTimerRef.current) clearTimeout(reverseTimerRef.current)
    }
  }, [])

  const distanceKm = useMemo(() => haversineKm(hostLocation, selected), [hostLocation, selected])
  const withinRadius = distanceKm <= deliveryRadiusKm
  const title =
    mode === 'pickup' ? t('checkout.pickup_location_title') : t('checkout.dropoff_location_title')

  const runSearch = useCallback((text: string) => {
    setQuery(text)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    const normalized = text.trim()
    if (normalized.length < 2) {
      setResults([])
      setHasSearched(false)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    searchTimerRef.current = setTimeout(async () => {
      try {
        const data = await geocodingService.forward(normalized)
        setResults(data)
        setHasSearched(true)
        productTelemetryService.recordEvent('search', 'booking.location_search_completed', {
          elementId: 'booking.location-picker.search',
          elementType: 'search',
          metadata: {
            queryLength: normalized.length,
            resultCount: data.length,
          },
        })
      } catch (err) {
        setResults([])
        setHasSearched(true)
        productTelemetryService.recordEvent('search', 'booking.location_search_failed', {
          elementId: 'booking.location-picker.search',
          elementType: 'search',
          metadata: { queryLength: normalized.length },
        })
        captureError(
          err,
          { screen: 'booking/location-picker', action: 'forward-geocode' },
          { queryLength: normalized.length },
        )
      } finally {
        setIsSearching(false)
      }
    }, 260)
  }, [])

  // Only a real drag reaches here now. It used to fire on programmatic recentres too — picking
  // an address from search moves the camera — so the reverse-geocode of that point immediately
  // overwrote the address the guest had just chosen, and spent an API call doing it.
  const handleMapMove = useCallback((lat: number, lng: number) => {
    setSelected((current) => ({ ...current, lat, lng }))
    if (reverseTimerRef.current) clearTimeout(reverseTimerRef.current)
    setIsResolvingPin(true)

    reverseTimerRef.current = setTimeout(async () => {
      try {
        const result = await geocodingService.reverse(lat, lng)
        setSelected({
          lat,
          lng,
          address: result?.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        })
      } catch (err) {
        setSelected((current) => ({
          ...current,
          address: current.address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
        }))
        captureError(
          err,
          { screen: 'booking/location-picker', action: 'reverse-geocode' },
          { lat: Number(lat.toFixed(5)), lng: Number(lng.toFixed(5)) },
        )
      } finally {
        setIsResolvingPin(false)
      }
    }, 420)
  }, [])

  const handleResultPress = useCallback(
    (result: GeocodingResult) => {
      setSelected({ address: result.address, lat: result.lat, lng: result.lng })
      setMapRevision((revision) => revision + 1)
      setQuery(result.address)
      setResults([])
      setHasSearched(false)
      productTelemetryService.recordEvent('booking_step', 'booking.location_result_selected', {
        elementId: 'booking.location-picker.result',
        elementType: 'button',
        metadata: {
          mode,
          withinRadius: isWithinDeliveryRadius({
            hostLocation,
            location: result,
            deliveryRadiusKm,
          }),
        },
      })
    },
    [deliveryRadiusKm, hostLocation, mode],
  )

  const handleSave = useCallback(() => {
    if (!withinRadius || !selected.address.trim()) return
    productTelemetryService.recordEvent('booking_step', 'booking.location_saved', {
      elementId: 'booking.location-picker.save',
      elementType: 'button',
      metadata: { mode, withinRadius },
    })
    onSave({
      address: selected.address.trim(),
      lat: selected.lat,
      lng: selected.lng,
    })
  }, [mode, onSave, selected, withinRadius])

  return (
    <AppModal
      visible={visible}
      animationType="slide"
      transparent={false}
      onRequestClose={onClose}
      testID={`booking.location-picker.${mode}`}
    >
      <LiquidCanvas isolate style={{ flex: 1, paddingTop: insets.top }}>
        <View
          style={{
            minHeight: 64,
            paddingHorizontal: SPACING.lg,
            paddingVertical: SPACING.md,
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.border,
          }}
        >
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: `${COLORS.coral.DEFAULT}12`,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MapPin size={20} color={COLORS.coral.DEFAULT} strokeWidth={2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 19,
                lineHeight: 24,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
              numberOfLines={1}
            >
              {title}
            </Text>
            <Text
              style={{
                marginTop: 2,
                fontSize: 13,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
              numberOfLines={1}
            >
              {t('checkout.search_or_drag_pin')}
            </Text>
          </View>
          <RNPressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t('common.close')}
            testID="booking.location-picker.close"
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: colors.surfaceSubtle,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={19} color={colors.textSecondary} strokeWidth={2} />
          </RNPressable>
        </View>

        <RNScrollView
          contentContainerStyle={{
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.md,
            paddingBottom: insets.bottom + SPACING.lg,
            gap: SPACING.md,
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <GlassSurface
            tone="quiet"
            style={{
              minHeight: 50,
              borderRadius: RADIUS.lg,
            }}
            contentStyle={{
              minHeight: 50,
              paddingHorizontal: SPACING.md,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
            }}
          >
            <Search size={18} color={colors.textMuted} strokeWidth={2} />
            <RNTextInput
              ref={inputRef}
              value={query}
              onChangeText={runSearch}
              placeholder={t('checkout.location_search_placeholder')}
              placeholderTextColor={colors.textMuted}
              autoCorrect={false}
              autoCapitalize="words"
              returnKeyType="search"
              accessibilityLabel={t('checkout.location_search_placeholder')}
              testID="booking.location-picker.search"
              style={{
                flex: 1,
                minWidth: 0,
                height: 50,
                fontSize: 16,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
              }}
            />
            {isSearching ? <ActivityIndicator size="small" color={COLORS.coral.DEFAULT} /> : null}
          </GlassSurface>

          {(results.length > 0 || (hasSearched && !isSearching)) && (
            <GlassSurface
              style={{
                borderRadius: RADIUS.lg,
                overflow: 'hidden',
              }}
            >
              {results.length === 0 ? (
                <Text
                  style={{
                    padding: SPACING.md,
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('checkout.location_no_results')}
                </Text>
              ) : (
                results.map((result, index) => (
                  <RNPressable
                    key={`${result.lat}-${result.lng}-${index}`}
                    onPress={() => handleResultPress(result)}
                    accessibilityRole="button"
                    accessibilityLabel={result.address}
                    testID={`booking.location-picker.result-${index}`}
                    style={{
                      minHeight: 58,
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.sm,
                      borderBottomWidth: index === results.length - 1 ? 0 : 1,
                      borderBottomColor: colors.border,
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.md,
                    }}
                  >
                    <MapPin size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          fontSize: 14,
                          lineHeight: 19,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                        numberOfLines={1}
                      >
                        {result.address}
                      </Text>
                      {result.city ? (
                        <Text
                          style={{
                            marginTop: 2,
                            fontSize: 12,
                            lineHeight: 16,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                          }}
                          numberOfLines={1}
                        >
                          {result.city}
                        </Text>
                      ) : null}
                    </View>
                  </RNPressable>
                ))
              )}
            </GlassSurface>
          )}

          <LocationMapPicker
            key={`${mode}:${mapRevision}`}
            center={[selected.lng, selected.lat]}
            onLocationPicked={handleMapMove}
          />

          <GlassSurface
            style={{
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: withinRadius ? colors.border : colors.error,
            }}
            contentStyle={{
              padding: SPACING.md,
              gap: SPACING.sm,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
              <MapPin size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{
                    fontSize: 12,
                    lineHeight: 16,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                  numberOfLines={1}
                >
                  {t('checkout.selected_location')}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontSize: 15,
                    lineHeight: 20,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {selected.address}
                </Text>
              </View>
              {isResolvingPin ? (
                <ActivityIndicator size="small" color={COLORS.coral.DEFAULT} />
              ) : null}
            </View>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              {withinRadius ? (
                <CheckCircle size={17} color={colors.success} strokeWidth={2} />
              ) : (
                <AlertCircle size={17} color={colors.textError} strokeWidth={2} />
              )}
              <Text
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  lineHeight: 18,
                  fontFamily: FONTS.body,
                  color: withinRadius ? colors.success : colors.error,
                }}
              >
                {withinRadius
                  ? t('checkout.location_within_radius', {
                      distance: distanceKm.toFixed(1),
                      radius: deliveryRadiusKm,
                    })
                  : t('checkout.location_out_of_radius_detail', {
                      distance: distanceKm.toFixed(1),
                      radius: deliveryRadiusKm,
                    })}
              </Text>
            </View>
          </GlassSurface>

          <View style={{ gap: SPACING.sm }}>
            <Button
              title={t('checkout.save_location')}
              size="lg"
              onPress={handleSave}
              disabled={!withinRadius || isResolvingPin || !selected.address.trim()}
              testID="booking.location-picker.save"
            />
            <Button
              title={t('common.cancel')}
              variant="text"
              size="md"
              onPress={onClose}
              testID="booking.location-picker.cancel"
            />
          </View>
        </RNScrollView>
      </LiquidCanvas>
    </AppModal>
  )
}
